// // // // // Stockchart_MUI.jsx

import React, { useState, useEffect } from "react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import { Box, Typography, Button, ButtonGroup } from "@mui/material";

const defaultPeriods = [
  { label: "1D" },
  { label: "1W" },
  { label: "1M" },
  { label: "3M" },
  { label: "1Y" },
  { label: "5Y" },
  { label: "YTD" }
];

const formatValue = (val, format = "number") => {
  if (val === undefined || val === null) return "–";
  if (format === "percentage") return `${val.toFixed(2)}%`;
  if (format === "currency") {
    return val.toLocaleString(undefined, { style: "currency", currency: "EUR" });
  }
  return val;
};

const CustomTooltip = ({
  active, label, tooltipFields, externalData, seriesData, comparisonKey, comparisonLabel
}) => {
  if (!active || !label || !tooltipFields) return null;

  const isoDate = new Date(label).toISOString().split("T")[0];
  const dataEntry = externalData?.find(d => d.date === isoDate);
  const seriesEntry = seriesData?.find(d => d.date === isoDate);

  const getValue = (field) => {
    const sourceMap = {
      data: dataEntry,
      seriesData: seriesEntry
    };
    return formatValue(sourceMap[field.source]?.[field.key], field.format);
  };

  const comparisonValue = seriesEntry?.[comparisonKey];

  return (
    <Box sx={{ background: "#fff", border: "1px solid #ccc", padding: 1, borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>Date: {label}</Typography>
      {tooltipFields.map((field, i) => (
        <Typography key={i} variant="body2">
          {field.label}: {getValue(field)}
        </Typography>
      ))}
      {comparisonValue !== undefined && (
        <Typography variant="body2">
          {comparisonLabel}: {formatValue(comparisonValue, "percentage")}
        </Typography>
      )}
    </Box>
  );
};

const StockChart = ({
  selectedPeriod,
  title = "",
  externalData,
  seriesData,
  seriesMergeKey = "value",
  seriesKey = "value",
  tooltipFields = [],
  periods = defaultPeriods,
  height = 300,
  yAxisFormat = "currency",
  currency = "€",
  colorUp = "#4caf50",
  colorDown = "#f44336",
  showLegend = true,
  showToolbar = true,
  onPeriodChange,
  comparisonSeries = null,
  comparisonKey = "value",
  comparisonLabel = "Comparison",
  comparisonColor = "#1976d2",
  subtitle = null
}) => {
  const activePeriod = periods.findIndex(p => p.label === selectedPeriod);

  
 
  
  // const [activePeriod, setActivePeriod] = useState(6);

  const start = seriesData?.[0]?.[seriesKey] ?? 0;
  const end = seriesData?.[seriesData.length - 1]?.[seriesKey] ?? 0;

  const valueChange = end - start;
  const percentChange = start !== 0 ? (valueChange / start) * 100 : 0;
  const isPositive = valueChange >= 0;

//   console.log("Incoming seriesData:", seriesData.slice(0, 5));
// console.log("seriesKey:", seriesKey);

  // ✅ Merge comparisonSeries into seriesData by date
  const mergedData = (seriesData || [])
  .filter(entry => typeof entry[seriesKey] === 'number' && !isNaN(entry[seriesKey]))
  .map(entry => {
    const match = comparisonSeries?.find(d => d.date === entry.date);

    // Only inject comparisonKey if it's not the same as seriesKey
    return comparisonSeries && comparisonKey !== seriesKey
      ? {
          ...entry,
          [comparisonKey]: match?.[comparisonKey] ?? null
        }
      : entry;
  });

  // console.log("Final mergedData to chart:", mergedData.slice(0, 5));


  

  // console.log("Rendered points:", mergedData.length, mergedData.slice(0, 5));


  return (
    <Box width="100%">
      {title && (
        <Typography variant="h6" mb={2} fontWeight="medium">{title}</Typography>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">{formatValue(end, yAxisFormat)}</Typography>
          <Typography variant="body2" color={isPositive ? "success.main" : "error.main"}>
            {isPositive ? "+" : "-"}{formatValue(Math.abs(valueChange), yAxisFormat)} (
            {isPositive ? "+" : "-"}{Math.abs(percentChange).toFixed(2)}%)
          </Typography>
          {subtitle}
        </Box>

        {showToolbar && (
          <ButtonGroup size="small" variant="outlined">
            {periods.map((period, index) => (
  <Button
    key={period.label}
    variant={activePeriod === index ? "contained" : "outlined"}
    onClick={() => onPeriodChange?.(period.label)}
  >
    {period.label}
  </Button>
))}

          </ButtonGroup>
        )}
      </Box>

      <Box height={height} width="100%">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={mergedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={isPositive ? colorUp : colorDown} stopOpacity={0.8} />
                <stop offset="95%" stopColor={isPositive ? colorUp : colorDown} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" vertical={false} />

            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
              tickFormatter={(value, index) =>
                seriesData.length > 30 && index % Math.ceil(seriesData.length / 6) !== 0 ? "" : value
              }
            />

              <YAxis
  tickFormatter={(val) => formatValue(val, yAxisFormat)}
  domain={
    yAxisFormat === "percentage"
      ? ["auto", "auto"]
      : yAxisFormat === "number"
      ? ["auto", "auto"]
      : ["dataMin - 5000", "dataMax + 5000"]
  }
  tickLine={false}
  axisLine={false}
  tick={{ fontSize: 12 }}
/>

            {/* <YAxis
              tickFormatter={(val) => formatValue(val, yAxisFormat)}
              domain={yAxisFormat === "percentage" ? ["auto", "auto"] : ["dataMin - 5000", "dataMax + 5000"]}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
            /> */}

            <Tooltip
              content={
                <CustomTooltip
                  tooltipFields={tooltipFields}
                  externalData={externalData}
                  seriesData={mergedData}
                  comparisonKey={comparisonKey}
                  comparisonLabel={comparisonLabel}
                />
              }
            />

            <Area
              type="monotone"
              dataKey={seriesKey}
              stroke={isPositive ? colorUp : colorDown}
              fillOpacity={1}
              fill="url(#colorValue)"
              name={tooltipFields?.[0]?.label || "Portfolio"}
            />

            {comparisonSeries && (
              <Area
                type="monotone"
                dataKey={comparisonKey}
                stroke={comparisonColor}
                fill="none"
                strokeDasharray="5 5"
                name={comparisonLabel}
              />
            )}

            {showLegend && <Legend />}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default StockChart;
